/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useMediaQuery} from 'react-responsive';
import {
  Button,
  Fab,
  CircularProgress,
  TextField,
  IconButton,
  InputAdornment,
  Divider,
  Drawer,
  MenuItem,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  updateProfile,
  signOut,
} from 'firebase/auth';
import InputMask from 'react-input-mask';
import styled, {keyframes} from 'styled-components';
import {Link} from 'react-router-dom';
import Tab from '../../Components/tab';
import Footer from '../../Components/Footer';
import logo from '../../Assets/logo.png';
import planosSolo from './planos/solo.json';
import planosFrota from './planos/frota.json';
import planosLoja from './planos/loja.json';
import planosEnterprise from './planos/enterprise.json';

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://kops.apiireit.com/gateway';

// ─── Animations ───────────────────────────────────────────────────────────────
const slideUp = keyframes`
  from { transform: translateY(32px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;
const SlideUp = styled.div`
  animation: ${slideUp} 0.55s ease-out both;
  animation-delay: ${p => p.delay || '0s'};
`;

// ─── Colors ───────────────────────────────────────────────────────────────────
const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  bg: '#f4f6fa',
  text: '#1a1a2e',
  muted: '#8a94a6',
  white: '#ffffff',
  success: '#059669',
};

// ══════════════════════════════════════════════════════════════════════════════
// CATÁLOGO DE PLANOS — lido de JSON estático (um arquivo por aba), NÃO da API.
// A rota /zonas/** exige login no gateway, então a página de preços (pública,
// sem login) não consegue chamar a API. O JSON é a fonte de verdade pra
// EXIBIÇÃO; se um plano mudar de preço/regra no banco, atualiza o JSON aqui
// também. O `id` real do Mongo só é resolvido depois do login (casando pelo
// `nome` na API autenticada), então não precisa manter id no JSON.
// ══════════════════════════════════════════════════════════════════════════════
const GRUPOS = [
  {id: 'solo', label: 'Motorista & Entregador', icon: '🛵'},
  {id: 'frota', label: 'Frotas', icon: '🚗'},
  {id: 'loja', label: 'Lojas & Delivery', icon: '🏪'},
  {id: 'enterprise', label: 'Super App & Franquias', icon: '🏢'},
];

const PLANOS_CATALOG = [
  ...planosSolo.map(p => ({...p, grupo: 'solo'})),
  ...planosFrota.map(p => ({...p, grupo: 'frota'})),
  ...planosLoja.map(p => ({...p, grupo: 'loja'})),
  ...planosEnterprise.map(p => ({...p, grupo: 'enterprise'})),
];

const MODULO_LABELS = {
  corridas: 'Gestão de corridas',
  entregas: 'Gestão de entregas',
  app_proprio_entregador: 'App próprio (entregador)',
  app_proprio_motorista: 'App próprio (motorista)',
  frota: 'Gestão de frota',
  gestao_motoristas: 'Gestão de motoristas',
  relatorios_avancados: 'Relatórios avançados',
  multinivel: 'Multinível (submaster)',
  multinivel_master: 'Recebe % da receita das unidades que você recrutar',
  white_label: 'App white-label',
  categoria_ireit: 'Categoria dentro do app Irê it',
  cardapio_digital: 'Cardápio digital',
  multi_loja: 'Múltiplas lojas numa conta',
  white_label_visual: 'Identidade visual própria',
  marketplace_ireit_opcional: 'Marketplace Irê it (opcional)',
  dashboard_grupo: 'Dashboard consolidado do grupo',
  territorio_regional: 'Território regional',
  exclusividade_regional: 'Exclusividade regional',
};

const NOME_TIPO_USO = {
  CORRIDA: 'Corridas',
  ENTII: 'Entregas (logística Irê it)',
  ENTREGA: 'Entregas (Flash)',
  PEDIDO: 'Pedidos (entrega própria)',
};

// ─── Formatters ───────────────────────────────────────────────────────────────
const formatBRL = valor =>
  `R$ ${Number(valor || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// `cobrancas` é uma lista — todas as entradas somam entre si (cumulativo,
// ex.: R$0,39 fixo + 1% no mesmo plano), por isso junta tudo com " + ".
const formatCobrancaVariavel = (cobrancas, unidade) => {
  if (!cobrancas || cobrancas.length === 0) return null;
  const partes = cobrancas
    .filter(c => c && c.valor)
    .map(c => {
      if (c.tipo === 'PERCENTUAL') {
        const pct = (c.valor * 100).toFixed(1).replace(/\.0$/, '');
        return `${pct}% sobre ${unidade}`;
      }
      return `${formatBRL(c.valor)} por ${unidade}`;
    });
  if (partes.length === 0) return null;
  return `+ ${partes.join(' + ')}`;
};

// Agrupa regras com faixas idênticas (ex.: ENTII e ENTREGA costumam ter os
// mesmos degraus) numa única tabela, pra não repetir a mesma tabela 2x com
// título diferente — vira 1 tabela com os nomes combinados no título.
const formatTabelasUso = regrasUso => {
  if (!regrasUso || regrasUso.length === 0) return [];

  const grupos = [];
  regrasUso.forEach(regra => {
    const faixas = [...(regra.faixas || [])].sort(
      (a, b) => a.usoMinimo - b.usoMinimo,
    );
    const assinatura = JSON.stringify(faixas);
    const nome = NOME_TIPO_USO[regra.tipo] || regra.tipo;
    const existente = grupos.find(g => g.assinatura === assinatura);
    if (existente) {
      existente.nomes.push(nome);
    } else {
      grupos.push({assinatura, nomes: [nome], faixas});
    }
  });

  return grupos.map(g => ({
    titulo: g.nomes.join(' / '),
    linhas: g.faixas.map((f, i) => {
      const proxima = g.faixas[i + 1];
      const faixaLabel = proxima
        ? `${f.usoMinimo}–${proxima.usoMinimo - 1}`
        : `${f.usoMinimo}+`;
      const valorLabel =
        f.valorMensal > 0 ? `${formatBRL(f.valorMensal)}/mês` : 'Grátis';
      return {faixaLabel, valorLabel};
    }),
  }));
};

const somenteDigitos = v => (v || '').replace(/\D/g, '');

const formatCpfCnpj = raw => {
  const d = somenteDigitos(raw).slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return d
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

// ══════════════════════════════════════════════════════════════════════════════
// SHARED UI BITS
// ══════════════════════════════════════════════════════════════════════════════
const STEPS = [
  {id: 'auth', label: 'Conta', icon: '🔐'},
  {id: 'empresa', label: 'Empresa', icon: '📋'},
  {id: 'endereco', label: 'Endereço', icon: '📍'},
  {id: 'bancario', label: 'Bancário', icon: '🏦'},
  {id: 'confirm', label: 'Confirmar', icon: '✅'},
];

const StepBar = ({current, sm}) => {
  const idx = STEPS.findIndex(s => s.id === current);
  if (idx === -1) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sm ? 4 : 8,
        marginBottom: 32,
        flexWrap: 'wrap',
      }}
    >
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <React.Fragment key={s.id}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <div
                style={{
                  width: sm ? 34 : 42,
                  height: sm ? 34 : 42,
                  borderRadius: '50%',
                  background: done
                    ? C.success
                    : active
                    ? C.laranja
                    : 'rgba(255,255,255,0.12)',
                  border: `2px solid ${
                    done
                      ? C.success
                      : active
                      ? C.laranja
                      : 'rgba(255,255,255,0.2)'
                  }`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: sm ? 14 : 18,
                  transition: 'all 0.3s ease',
                }}
              >
                {done ? '✓' : s.icon}
              </div>
              {!sm && (
                <span
                  style={{
                    fontSize: 11,
                    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {s.label}
                </span>
              )}
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  width: sm ? 16 : 32,
                  height: 2,
                  marginBottom: sm ? 0 : 18,
                  background: i < idx ? C.success : 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  transition: 'background 0.3s ease',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const Card = ({children, sm, maxWidth = 520}) => (
  <div
    style={{
      background: C.white,
      borderRadius: sm ? 16 : 24,
      padding: sm ? '24px 16px' : '36px 32px',
      maxWidth,
      width: '100%',
      margin: '0 auto',
      boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
      boxSizing: 'border-box',
    }}
  >
    {children}
  </div>
);

const STitle = ({children, sub}) => (
  <div style={{marginBottom: 24}}>
    <p style={{margin: 0, fontSize: 20, fontWeight: 800, color: C.text}}>
      {children}
    </p>
    {sub && (
      <p style={{margin: '4px 0 0', fontSize: 14, color: C.muted}}>{sub}</p>
    )}
  </div>
);

const NavRow = ({
  onBack,
  onNext,
  nextLabel = 'Continuar',
  nextDisabled,
  loading,
}) => (
  <div
    style={{
      display: 'flex',
      gap: 12,
      marginTop: 28,
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    {onBack ? (
      <Button
        onClick={onBack}
        startIcon={<ArrowBack />}
        style={{textTransform: 'none', color: C.muted, fontSize: 14}}
      >
        Voltar
      </Button>
    ) : (
      <span />
    )}
    <Button
      onClick={onNext}
      disabled={nextDisabled || loading}
      endIcon={loading ? null : <ArrowForward />}
      sx={{
        bgcolor: C.laranja,
        color: '#fff',
        borderRadius: '12px',
        height: 48,
        px: 3,
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 15,
        ':hover': {bgcolor: C.laranjaD},
        ':disabled': {bgcolor: '#e0e0e0', color: '#aaa'},
      }}
    >
      {loading ? (
        <CircularProgress size={20} style={{color: '#fff'}} />
      ) : (
        nextLabel
      )}
    </Button>
  </div>
);

const benefits = [
  {icon: '🎁', text: '7 dias grátis'},
  {icon: '🚫', text: 'Sem fidelidade'},
  {icon: '⚙️', text: 'Você escolhe: fixo, por uso ou %'},
  {icon: '📊', text: 'Painel completo'},
  {icon: '💬', text: 'Suporte dedicado'},
];

// ══════════════════════════════════════════════════════════════════════════════
// PRICING CARD
// ══════════════════════════════════════════════════════════════════════════════
const formatAdicional = a => {
  if (!a) return '';
  switch (a.tipoCobranca) {
    case 'INCLUSO':
      return a.nome;
    case 'FIXO_MENSAL':
      return `${a.nome} — ${formatBRL(a.valor)}/mês`;
    case 'FIXO_UNIDADE':
      return `${a.nome} — ${formatBRL(a.valor)} por unidade`;
    case 'PERCENTUAL': {
      const pct = (a.valor * 100).toFixed(1).replace(/\.0$/, '');
      return `${a.nome} — ${pct}%`;
    }
    case 'SOB_CONSULTA':
    default:
      return `${a.nome} (consultar)`;
  }
};

const PricingCard = ({plano, selected, onEscolher, sm}) => {
  const [mostrarOpcionais, setMostrarOpcionais] = useState(false);
  const tabelasUso = formatTabelasUso(plano.regrasUso);
  const corrida = formatCobrancaVariavel(plano.cobrancaCorrida, 'corrida');
  const pedido = formatCobrancaVariavel(plano.cobrancaPedido, 'pedido (venda)');
  const features = (plano.modulosLiberados || []).map(
    m => MODULO_LABELS[m] || m,
  );
  const adicionais = plano.adicionais || [];

  return (
    <div
      style={{
        background: C.white,
        borderRadius: 20,
        padding: '26px 22px',
        border: selected
          ? `2px solid ${C.laranja}`
          : '1px solid rgba(0,0,0,0.08)',
        boxShadow: selected
          ? '0 12px 32px rgba(255,102,0,0.18)'
          : '0 4px 16px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'box-shadow 0.2s, border 0.2s',
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      {plano.badge && (
        <span
          style={{
            position: 'absolute',
            top: -12,
            left: 20,
            background: C.laranja,
            color: '#fff',
            fontSize: 11,
            fontWeight: 800,
            padding: '5px 14px',
            borderRadius: 20,
            letterSpacing: '0.02em',
            boxShadow: '0 4px 10px rgba(255,102,0,0.3)',
          }}
        >
          {plano.badge}
        </span>
      )}

      <div
        style={{
          fontSize: 30,
          marginBottom: 6,
          marginTop: plano.badge ? 6 : 0,
        }}
      >
        {plano.icone || '⭐'}
      </div>
      <p style={{margin: 0, fontSize: 18, fontWeight: 800, color: C.text}}>
        {plano.nome}
      </p>
      <p
        style={{
          margin: '6px 0 16px',
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.5,
          minHeight: 40,
        }}
      >
        {plano.descricao}
      </p>

      {/* Preço */}
      <div style={{marginBottom: 14}}>
        {plano.precoMensal > 0 ? (
          <>
            <span style={{fontSize: 26, fontWeight: 800, color: C.azul}}>
              {formatBRL(plano.precoMensal)}
            </span>
            <span style={{fontSize: 13, color: C.muted}}>/mês</span>
          </>
        ) : (
          <span style={{fontSize: 22, fontWeight: 800, color: C.success}}>
            Sob demanda de uso
          </span>
        )}
        {plano.precoSetup > 0 && (
          <p style={{margin: '4px 0 0', fontSize: 12, color: C.muted}}>
            + {formatBRL(plano.precoSetup)} taxa de setup (única)
          </p>
        )}
        {(corrida || pedido) && (
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 12,
              color: C.laranjaD,
              fontWeight: 700,
            }}
          >
            {[corrida, pedido].filter(Boolean).join(' · ')}
          </p>
        )}
        <p
          style={{
            margin: '6px 0 0',
            fontSize: 11.5,
            fontWeight: 600,
            color: plano.fidelidadeMeses > 0 ? C.muted : C.success,
          }}
        >
          {plano.fidelidadeMeses > 0
            ? `🔒 Fidelidade de ${plano.fidelidadeMeses} ${
                plano.fidelidadeMeses === 1 ? 'mês' : 'meses'
              }`
            : '✅ Sem fidelidade'}
        </p>
      </div>

      {/* Faixas de uso */}
      {tabelasUso.length > 0 && (
        <div style={{marginBottom: 14}}>
          {tabelasUso.map((t, idx) => (
            <div
              key={t.titulo}
              style={{
                background: '#f4f6fa',
                borderRadius: 10,
                padding: '8px 12px 4px',
                marginBottom: idx < tabelasUso.length - 1 ? 8 : 0,
              }}
            >
              <p
                style={{
                  margin: '0 0 4px',
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: C.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {t.titulo}
              </p>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <tbody>
                  {t.linhas.map((l, i) => (
                    <tr
                      key={l.faixaLabel}
                      style={{
                        borderTop: i === 0 ? 'none' : '1px solid #e2e6ee',
                      }}
                    >
                      <td
                        style={{
                          padding: '4px 6px 4px 0',
                          fontSize: 12,
                          color: C.muted,
                        }}
                      >
                        {l.faixaLabel}
                      </td>
                      <td
                        style={{
                          padding: '4px 0',
                          fontSize: 12,
                          fontWeight: 700,
                          textAlign: 'right',
                          color: l.valorLabel === 'Grátis' ? C.success : C.text,
                        }}
                      >
                        {l.valorLabel}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Features */}
      <div style={{flex: 1, marginBottom: 18}}>
        {features.map(f => (
          <div
            key={f}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              marginBottom: 7,
            }}
          >
            <CheckCircleOutline
              style={{
                fontSize: 16,
                color: C.success,
                marginTop: 1,
                flexShrink: 0,
              }}
            />
            <span style={{fontSize: 13, color: C.text, lineHeight: 1.4}}>
              {f}
            </span>
          </div>
        ))}
      </div>

      {/* Opcionais (adicionais) — expansível */}
      {adicionais.length > 0 && (
        <div style={{marginBottom: 14}}>
          <button
            onClick={() => setMostrarOpcionais(v => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              background: 'none',
              border: 'none',
              borderTop: '1px solid #eef0f4',
              cursor: 'pointer',
              padding: '10px 0 0',
              fontSize: 12.5,
              fontWeight: 700,
              color: C.azul,
              fontFamily: "'Outfit','Segoe UI',sans-serif",
            }}
          >
            <span>
              {mostrarOpcionais
                ? 'Ocultar opcionais'
                : `Ver opcionais (${adicionais.length})`}
            </span>
            <span
              style={{
                display: 'inline-block',
                transition: 'transform 0.2s',
                transform: mostrarOpcionais ? 'rotate(180deg)' : 'none',
              }}
            >
              ▾
            </span>
          </button>
          {mostrarOpcionais && (
            <div style={{marginTop: 10}}>
              {adicionais.map(a => (
                <div
                  key={a.chave}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <span style={{fontSize: 12, color: C.muted, marginTop: 1}}>
                    +
                  </span>
                  <span
                    style={{fontSize: 12.5, color: C.text, lineHeight: 1.4}}
                  >
                    {formatAdicional(a)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Button
        fullWidth
        onClick={() => onEscolher(plano)}
        sx={{
          bgcolor: selected ? C.success : C.azul,
          color: '#fff',
          borderRadius: '12px',
          height: 46,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 14,
          ':hover': {bgcolor: selected ? C.success : C.azulD},
        }}
      >
        {selected ? '✓ Plano selecionado' : 'Escolher esse plano'}
      </Button>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
const RegisterGestor = () => {
  const auth = getAuth();
  const sm = useMediaQuery({query: '(max-width: 750.98px)'});
  const vsm = useMediaQuery({query: '(max-width: 450px)'});

  const [toggleBar, setToggleBar] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // ── planos ────────────────────────────────────────────────────────────────
  // Catálogo vem do JSON estático (ver import no topo) — a página de preços é
  // pública e o gateway exige login pra qualquer rota /zonas/**, então não dá
  // pra buscar da API aqui. O id real do Mongo é resolvido só depois do
  // login (ver resolvePlanoReal), casando pelo nome do plano.
  const [grupoAtivo, setGrupoAtivo] = useState('solo');
  const [selectedPlano, setSelectedPlano] = useState(null);
  const wizardRef = useRef(null);
  const pricingRef = useRef(null);

  // ── wizard ────────────────────────────────────────────────────────────────
  const [step, setStep] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [gestorExistente, setGestorExistente] = useState(null);

  // ── auth state ────────────────────────────────────────────────────────────
  const [authMode, setAuthMode] = useState('register');
  const [authName, setAuthName] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPass, setAuthPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  // ── empresa ───────────────────────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');
  const [dob, setDob] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [empresaError, setEmpresaError] = useState('');

  // ── endereço ──────────────────────────────────────────────────────────────
  const [cep, setCep] = useState('');
  const [cepError, setCepError] = useState(false);
  const [estado, setEstado] = useState('');
  const [cidade, setCidade] = useState('');
  const [bairro, setBairro] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [comp, setComp] = useState('');
  const [addrError, setAddrError] = useState('');

  // ── bancário ──────────────────────────────────────────────────────────────
  const [banco, setBanco] = useState('');
  const [agencia, setAgencia] = useState('');
  const [conta, setConta] = useState('');
  const [tConta, setTConta] = useState('Corrente');
  const [pix, setPix] = useState('');
  const [bancoError, setBancoError] = useState('');

  // ── submit ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ─────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
    signOut(auth).catch(() => {});
  }, []);

  useEffect(() => {
    const t = () => setIsVisible(window.pageYOffset > 50);
    window.addEventListener('scroll', t);
    return () => window.removeEventListener('scroll', t);
  }, []);

  // ── CEP lookup ────────────────────────────────────────────────────────────
  const fetchCep = useCallback(rawCep => {
    const clean = rawCep.replace(/\D/g, '');
    if (clean.length !== 8) return;
    fetch(`https://viacep.com.br/ws/${clean}/json/`)
      .then(r => r.json())
      .then(d => {
        if (d.erro) {
          setCepError(true);
          return;
        }
        setCepError(false);
        setEstado(d.uf || '');
        setCidade(d.localidade || '');
        setLogradouro(d.logradouro || '');
        setBairro(d.bairro || '');
      })
      .catch(() => setCepError(true));
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // RESOLVE PLANO REAL
  // O JSON estático não tem o id do Mongo (evita ficar desatualizado). Assim
  // que temos um token válido (pós-login), busca o catálogo autenticado e
  // casa pelo nome pra descobrir o id real — só ele é usado no envio final.
  // ─────────────────────────────────────────────────────────────────────────
  const resolvePlanoReal = async token => {
    if (!selectedPlano) return true;
    try {
      const res = await fetch(
        `${API_BASE}/zonas/planos/list?apenasAtivos=true`,
        {
          headers: {Authorization: `Bearer ${token}`},
        },
      );
      if (!res.ok) return false;
      const lista = await res.json();
      const real = (lista || []).find(p => p.nome === selectedPlano.nome);
      if (!real) return false;
      setSelectedPlano(prev => ({...prev, id: real.id}));
      return true;
    } catch {
      return false;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CHECK EXISTING GESTOR
  // GET /zonas/get/categories?ownerid={uid} → lista de zonas do dono
  // GET /zonas/assinatura/get?zonaId={id} → status da assinatura
  // ─────────────────────────────────────────────────────────────────────────
  const checkExistingGestor = async firebaseUser => {
    try {
      const token = await firebaseUser.getIdToken();
      const resZona = await fetch(
        `${API_BASE}/zonas/get/categories?ownerid=${firebaseUser.uid}`,
        {headers: {Authorization: `Bearer ${token}`}},
      );
      if (!resZona.ok) return false;
      const zonas = await resZona.json();
      if (!zonas || zonas.length === 0) return false;

      const zona = zonas[0];
      const resAss = await fetch(
        `${API_BASE}/zonas/assinatura/get?zonaId=${zona._id}`,
        {headers: {Authorization: `Bearer ${token}`}},
      );
      const assinatura = resAss.ok ? await resAss.json() : null;

      setGestorExistente({zona, assinatura});
      if (assinatura && ['TRIAL', 'ATIVA'].includes(assinatura.status)) {
        setStep('redirect');
      } else {
        setStep('existing');
      }
      return true;
    } catch {
      return false;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Roda logo após qualquer método de login: resolve o id real do plano
  // escolhido e decide se segue pro cadastro ou mostra status existente.
  // ─────────────────────────────────────────────────────────────────────────
  const afterAuthSuccess = async firebaseUser => {
    const token = await firebaseUser.getIdToken();
    const planoOk = await resolvePlanoReal(token);

    if (!planoOk) {
      setAuthError(
        'Não foi possível confirmar o plano selecionado. Tente novamente.',
      );
      return;
    }

    const hasExisting = await checkExistingGestor(firebaseUser);
    if (!hasExisting) setStep('empresa');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // AUTH HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const handleEmailAuth = async () => {
    setAuthError('');
    if (!authEmail.trim()) return setAuthError('Email obrigatório.');
    if (!authPass) return setAuthError('Senha obrigatória.');
    if (authMode === 'register' && !authName.trim())
      return setAuthError('Informe seu nome.');
    if (authMode === 'register' && authPass.length < 6)
      return setAuthError('Senha: mínimo 6 caracteres.');
    setAuthLoading(true);
    try {
      let cred;
      if (authMode === 'register') {
        cred = await createUserWithEmailAndPassword(
          auth,
          authEmail.trim(),
          authPass,
        );
        await updateProfile(cred.user, {displayName: authName.trim()});
      } else {
        cred = await signInWithEmailAndPassword(
          auth,
          authEmail.trim(),
          authPass,
        );
      }
      setAuthUser(cred.user);
      setEmail(cred.user.email || '');
      setName(cred.user.displayName || authName);
      await afterAuthSuccess(cred.user);
    } catch (err) {
      const map = {
        'auth/email-already-in-use': 'Email já cadastrado. Faça login.',
        'auth/wrong-password': 'Senha incorreta.',
        'auth/user-not-found': 'Usuário não encontrado.',
        'auth/invalid-credential': 'Email ou senha incorretos.',
        'auth/too-many-requests': 'Muitas tentativas. Tente mais tarde.',
      };
      setAuthError(map[err.code] || 'Erro ao autenticar.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogle = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await signInWithPopup(auth, new GoogleAuthProvider());
      setAuthUser(res.user);
      setEmail(res.user.email || '');
      setName(res.user.displayName || '');
      await afterAuthSuccess(res.user);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user')
        setAuthError('Erro ao entrar com Google.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleApple = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const res = await signInWithPopup(auth, provider);
      setAuthUser(res.user);
      setEmail(res.user.email || '');
      await afterAuthSuccess(res.user);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user')
        setAuthError('Erro ao entrar com Apple.');
    } finally {
      setAuthLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // VALIDATION
  // ─────────────────────────────────────────────────────────────────────────
  const validateEmpresa = () => {
    if (!title.trim())
      return setEmpresaError('Informe o nome da empresa/negócio.'), false;
    if (!name.trim())
      return setEmpresaError('Informe seu nome completo.'), false;
    if (somenteDigitos(cpf).length !== 11 && somenteDigitos(cpf).length !== 14)
      return setEmpresaError('CPF/CNPJ inválido.'), false;
    if (!rg.trim()) return setEmpresaError('Informe o RG.'), false;
    if (!telefone.trim())
      return setEmpresaError('Informe o celular/WhatsApp.'), false;
    if (!email.trim()) return setEmpresaError('Informe o email.'), false;
    setEmpresaError('');
    return true;
  };

  const validateEndereco = () => {
    if (!cep || cepError) return setAddrError('CEP inválido.'), false;
    if (!cidade.trim()) return setAddrError('Informe a cidade.'), false;
    if (!logradouro.trim()) return setAddrError('Informe o logradouro.'), false;
    if (!numero.trim()) return setAddrError('Informe o número.'), false;
    setAddrError('');
    return true;
  };

  const validateBancario = () => {
    if (!banco.trim()) return setBancoError('Informe o banco.'), false;
    if (!agencia.trim()) return setBancoError('Informe a agência.'), false;
    if (!conta.trim()) return setBancoError('Informe a conta.'), false;
    setBancoError('');
    return true;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SUBMIT
  // ─────────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!authUser || !selectedPlano) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const token = await authUser.getIdToken();
      const enderecoCompleto = `${logradouro}${
        comp ? `, ${comp}` : ''
      }, ${numero} — ${bairro}`;

      const payload = {
        zona: {
          ownerid: authUser.uid,
          tx: 0,
          txinv: 0,
          title,
          name,
          cpf: somenteDigitos(cpf),
          dob,
          telefone,
          email,
          address: enderecoCompleto,
          banco,
          agencia,
          conta,
          pix,
        },
        employer: {
          cdsid: authUser.uid,
          rg,
          end: `${logradouro}${comp ? `, ${comp}` : ''}`,
          num: parseInt(numero, 10) || 0,
          bairro,
          cid: cidade,
          estado,
          pais: 'Brasil',
          TConta: tConta,
        },
      };

      const res = await fetch(
        `${API_BASE}/zonas/onboarding/cadastro?planoId=${selectedPlano.id}&cicloCobranca=MENSAL`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) throw new Error(`status ${res.status}`);

      setStep('done');
    } catch (err) {
      setSubmitError(
        'Erro ao enviar. Verifique sua conexão e tente novamente.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // PLAN SELECTION
  // ─────────────────────────────────────────────────────────────────────────
  const handleEscolherPlano = plano => {
    setSelectedPlano(plano);
    setStep('auth');
    setTimeout(() => {
      wizardRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 80);
  };

  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
  };

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER — status cards (já tem gestor cadastrado)
  // ──────────────────────────────────────────────────────────────────────────
  const STATUS_LABELS = {
    INADIMPLENTE: {
      label: 'Inadimplente',
      color: '#c2410c',
      bg: '#fff7ed',
      icon: '⚠️',
      msg: 'Há uma fatura em aberto. Regularize para voltar a acessar o painel.',
    },
    BLOQUEADA: {
      label: 'Bloqueada',
      color: '#d97706',
      bg: '#fef3c7',
      icon: '🔒',
      msg: 'Sua conta está bloqueada. Entre em contato com o suporte.',
    },
    CANCELADA: {
      label: 'Cancelada',
      color: '#6b7280',
      bg: '#f3f4f6',
      icon: '❌',
      msg: 'Sua assinatura foi cancelada. Fale com o suporte para reativar.',
    },
  };

  const renderExisting = () => {
    const g = gestorExistente;
    const status = g?.assinatura?.status;
    const stt = STATUS_LABELS[status] || STATUS_LABELS.CANCELADA;
    return (
      <SlideUp delay="0.05s">
        <Card sm={sm}>
          <div style={{textAlign: 'center', marginBottom: 24}}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: stt.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 32,
                margin: '0 auto 14px',
                border: `2px solid ${stt.color}30`,
              }}
            >
              {stt.icon}
            </div>
            <p
              style={{margin: 0, fontSize: 20, fontWeight: 800, color: C.text}}
            >
              Você já tem uma conta de gestor
            </p>
            <p style={{margin: '6px 0 0', fontSize: 14, color: C.muted}}>
              Cada conta pode ter apenas uma zona vinculada.
            </p>
          </div>

          <div
            style={{
              padding: '12px 16px',
              borderRadius: 10,
              background: stt.bg,
              border: `1px solid ${stt.color}30`,
              marginBottom: 24,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: stt.color,
                fontWeight: 600,
              }}
            >
              {stt.msg}
            </p>
          </div>

          <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
            <Button
              href="mailto:suporte@ireit.com.br"
              sx={{
                bgcolor: C.azul,
                color: '#fff',
                borderRadius: '12px',
                height: 46,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 14,
                ':hover': {bgcolor: C.azulD},
              }}
            >
              Falar com suporte
            </Button>
            <Button
              component={Link}
              to="/"
              sx={{
                color: C.muted,
                borderRadius: '12px',
                height: 46,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                ':hover': {color: C.text},
              }}
            >
              Voltar ao início
            </Button>
          </div>
        </Card>
      </SlideUp>
    );
  };

  const renderRedirect = () => {
    const g = gestorExistente;
    return (
      <SlideUp delay="0.05s">
        <Card sm={sm}>
          <div style={{textAlign: 'center', padding: '8px 0 24px'}}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: '#ecfdf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 32,
                margin: '0 auto 16px',
                border: '2px solid #05966930',
              }}
            >
              ✅
            </div>
            <p
              style={{margin: 0, fontSize: 20, fontWeight: 800, color: C.text}}
            >
              {g?.assinatura?.status === 'TRIAL'
                ? 'Seu período de testes está ativo!'
                : 'Sua conta está ativa!'}
            </p>
            <p
              style={{
                margin: '8px auto 0',
                fontSize: 14,
                color: C.muted,
                maxWidth: 340,
                lineHeight: 1.6,
              }}
            >
              O gerenciamento é feito pelo painel exclusivo para gestores.
              Clique abaixo para acessar.
            </p>

            <Button
              href="https://myireit.com.br"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: C.laranja,
                color: '#fff',
                borderRadius: '12px',
                height: 52,
                px: 4,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 15,
                width: '100%',
                mt: 3,
                mb: 1,
                ':hover': {bgcolor: C.laranjaD},
              }}
            >
              Acessar painel do gestor →
            </Button>
            <p style={{margin: '10px 0 0', fontSize: 12, color: C.muted}}>
              myireit.com.br · Use a mesma conta Irê it para entrar
            </p>
          </div>
        </Card>
      </SlideUp>
    );
  };

  // ── STEP: Auth ────────────────────────────────────────────────────────────
  const renderAuth = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <div
          style={{
            display: 'flex',
            borderRadius: 12,
            background: '#f4f6fa',
            padding: 4,
            marginBottom: 24,
          }}
        >
          {['register', 'login'].map(m => (
            <button
              key={m}
              onClick={() => {
                setAuthMode(m);
                setAuthError('');
              }}
              style={{
                flex: 1,
                padding: '9px 0',
                border: 'none',
                borderRadius: 9,
                background: authMode === m ? '#fff' : 'transparent',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 14,
                color: authMode === m ? C.azul : C.muted,
                boxShadow:
                  authMode === m ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.2s',
                fontFamily: "'Outfit','Segoe UI',sans-serif",
              }}
            >
              {m === 'register' ? 'Criar conta' : 'Já tenho conta'}
            </button>
          ))}
        </div>

        <STitle sub="Usaremos sua conta para identificar sua empresa e a assinatura.">
          {authMode === 'register'
            ? 'Crie sua conta de gestor'
            : 'Entre na sua conta'}
        </STitle>

        {authMode === 'register' && (
          <TextField
            label="Seu nome completo"
            variant="outlined"
            size="small"
            fullWidth
            style={{marginBottom: 14}}
            value={authName}
            disabled={authLoading}
            onChange={e => setAuthName(e.target.value)}
            autoComplete="name"
          />
        )}

        <TextField
          label="Email"
          type="email"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 14}}
          value={authEmail}
          disabled={authLoading}
          onChange={e => setAuthEmail(e.target.value)}
          autoComplete="email"
        />

        <TextField
          label="Senha"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 8}}
          type={showPass ? 'text' : 'password'}
          value={authPass}
          disabled={authLoading}
          onChange={e => setAuthPass(e.target.value)}
          autoComplete={
            authMode === 'register' ? 'new-password' : 'current-password'
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPass(v => !v)}
                  edge="end"
                >
                  {showPass ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') handleEmailAuth();
          }}
        />

        {authError && (
          <p
            style={{
              color: '#e53935',
              fontSize: 13,
              textAlign: 'center',
              marginBottom: 8,
              marginTop: 4,
            }}
          >
            {authError}
          </p>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleEmailAuth}
          disabled={authLoading}
          sx={{
            bgcolor: C.azul,
            ':hover': {bgcolor: C.azulD},
            borderRadius: '12px',
            height: 50,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 16,
            mt: '8px',
          }}
        >
          {authLoading ? (
            <CircularProgress size={22} style={{color: '#fff'}} />
          ) : authMode === 'register' ? (
            'Criar conta e continuar'
          ) : (
            'Entrar e continuar'
          )}
        </Button>

        <Divider style={{margin: '18px 0'}}>
          <span style={{fontSize: 12, color: C.muted, padding: '0 8px'}}>
            ou continuar com
          </span>
        </Divider>

        <button
          onClick={handleGoogle}
          disabled={authLoading}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 12,
            border: '1px solid #e0e0e0',
            background: '#fff',
            color: '#3c4043',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
            fontFamily: "'Outfit','Segoe UI',sans-serif",
            marginBottom: 10,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
          onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continuar com Google
        </button>

        <button
          onClick={handleApple}
          disabled={authLoading}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 12,
            border: 'none',
            background: '#000',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
            fontFamily: "'Outfit','Segoe UI',sans-serif",
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#222')}
          onMouseLeave={e => (e.currentTarget.style.background = '#000')}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
          Continuar com Apple
        </button>
      </Card>
    </SlideUp>
  );

  // ── STEP: Empresa ─────────────────────────────────────────────────────────
  const renderEmpresa = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <STitle sub="Dados da empresa/negócio e do responsável.">
          Dados da Empresa
        </STitle>

        <TextField
          label="Nome da empresa/negócio *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 16}}
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Ex: Frota Central Ltda"
        />

        <TextField
          label="Seu nome completo (responsável) *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 16}}
          value={name}
          onChange={e => setName(e.target.value)}
        />

        <div style={{display: 'flex', gap: 12, marginBottom: 16}}>
          <TextField
            label="CPF ou CNPJ *"
            variant="outlined"
            size="small"
            style={{flex: 1}}
            value={cpf}
            onChange={e => setCpf(formatCpfCnpj(e.target.value))}
            placeholder="000.000.000-00"
          />
          <TextField
            label="RG *"
            variant="outlined"
            size="small"
            style={{flex: 1}}
            value={rg}
            onChange={e => setRg(e.target.value)}
          />
        </div>

        <InputMask
          mask="99/99/9999"
          value={dob}
          onChange={e => setDob(e.target.value)}
        >
          {() => (
            <TextField
              label="Data de nascimento"
              variant="outlined"
              size="small"
              fullWidth
              style={{marginBottom: 16}}
              placeholder="DD/MM/AAAA"
            />
          )}
        </InputMask>

        <TextField
          label="Celular / WhatsApp *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 16}}
          value={telefone}
          onChange={e => setTelefone(e.target.value)}
          placeholder="(71) 99999-9999"
        />

        <TextField
          label="Email *"
          type="email"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 4}}
          value={email}
          onChange={e => setEmail(e.target.value)}
        />

        {empresaError && (
          <p
            style={{
              color: '#e53935',
              fontSize: 13,
              marginTop: 12,
              marginBottom: 0,
            }}
          >
            {empresaError}
          </p>
        )}

        <NavRow
          onBack={() => setStep('auth')}
          onNext={() => {
            if (validateEmpresa()) setStep('endereco');
          }}
        />
      </Card>
    </SlideUp>
  );

  // ── STEP: Endereço ────────────────────────────────────────────────────────
  const renderEndereco = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <STitle sub="Endereço da sede/base do negócio.">Endereço</STitle>

        <InputMask
          mask="99999-999"
          value={cep}
          onChange={e => {
            setCep(e.target.value);
            const clean = e.target.value.replace(/\D/g, '');
            if (clean.length === 8) fetchCep(clean);
            else if (clean.length > 0) setCepError(true);
            else setCepError(false);
          }}
        >
          {() => (
            <TextField
              label="CEP *"
              variant="outlined"
              size="small"
              fullWidth
              style={{marginBottom: 6}}
              error={cepError}
              helperText={cepError ? 'CEP não encontrado.' : ''}
            />
          )}
        </InputMask>

        <Button
          size="small"
          style={{
            textTransform: 'none',
            color: C.laranja,
            fontSize: 12,
            marginBottom: 14,
          }}
          target="_blank"
          href="https://buscacepinter.correios.com.br/app/endereco/index.php"
        >
          Não sabe o CEP? Busque aqui
        </Button>

        <div style={{display: 'flex', gap: 12, marginBottom: 14}}>
          <TextField
            label="Estado"
            variant="outlined"
            size="small"
            style={{flex: '0 0 80px'}}
            value={estado}
            onChange={e => setEstado(e.target.value)}
            disabled
          />
          <TextField
            label="Cidade *"
            variant="outlined"
            size="small"
            style={{flex: 1}}
            value={cidade}
            onChange={e => setCidade(e.target.value)}
            disabled
          />
        </div>

        <TextField
          label="Bairro"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 14}}
          value={bairro}
          onChange={e => setBairro(e.target.value)}
        />

        <TextField
          label="Logradouro *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 14}}
          value={logradouro}
          onChange={e => setLogradouro(e.target.value)}
          placeholder="Rua, Avenida, Praça..."
        />

        <div style={{display: 'flex', gap: 12, marginBottom: 14}}>
          <TextField
            label="Número *"
            variant="outlined"
            size="small"
            style={{flex: '0 0 100px'}}
            value={numero}
            onChange={e => setNumero(e.target.value)}
            type="number"
          />
          <TextField
            label="Complemento"
            variant="outlined"
            size="small"
            style={{flex: 1}}
            value={comp}
            onChange={e => setComp(e.target.value)}
            placeholder="Sala, Loja..."
          />
        </div>

        {addrError && (
          <p style={{color: '#e53935', fontSize: 13, marginBottom: 0}}>
            {addrError}
          </p>
        )}

        <NavRow
          onBack={() => setStep('empresa')}
          onNext={() => {
            if (validateEndereco()) setStep('bancario');
          }}
        />
      </Card>
    </SlideUp>
  );

  // ── STEP: Bancário ────────────────────────────────────────────────────────
  const renderBancario = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <STitle sub="Conta para receber os repasses de faturamento da plataforma.">
          Dados Bancários
        </STitle>

        <TextField
          label="Banco *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 16}}
          value={banco}
          onChange={e => setBanco(e.target.value)}
          placeholder="Ex: 260 - Nubank"
        />

        <div style={{display: 'flex', gap: 12, marginBottom: 16}}>
          <TextField
            label="Agência *"
            variant="outlined"
            size="small"
            style={{flex: 1}}
            value={agencia}
            onChange={e => setAgencia(e.target.value)}
          />
          <TextField
            label="Conta *"
            variant="outlined"
            size="small"
            style={{flex: 1}}
            value={conta}
            onChange={e => setConta(e.target.value)}
          />
        </div>

        <TextField
          select
          label="Tipo de conta *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 16}}
          value={tConta}
          onChange={e => setTConta(e.target.value)}
        >
          <MenuItem value="Corrente">Corrente</MenuItem>
          <MenuItem value="Poupança">Poupança</MenuItem>
        </TextField>

        <TextField
          label="Chave PIX (opcional)"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 4}}
          value={pix}
          onChange={e => setPix(e.target.value)}
        />

        {bancoError && (
          <p
            style={{
              color: '#e53935',
              fontSize: 13,
              marginTop: 12,
              marginBottom: 0,
            }}
          >
            {bancoError}
          </p>
        )}

        <NavRow
          onBack={() => setStep('endereco')}
          onNext={() => {
            if (validateBancario()) setStep('confirm');
          }}
        />
      </Card>
    </SlideUp>
  );

  // ── STEP: Confirm ─────────────────────────────────────────────────────────
  const renderConfirm = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <STitle sub="Revise e envie seu cadastro.">Resumo do Cadastro</STitle>

        {[
          {label: 'Plano', value: selectedPlano?.nome},
          {label: 'Empresa', value: title},
          {label: 'Responsável', value: name},
          {label: 'Celular', value: telefone},
          {label: 'Email', value: email},
          {label: 'Cidade', value: `${cidade}, ${estado}`},
          {
            label: 'Endereço',
            value: `${logradouro}, ${numero}${bairro ? ` — ${bairro}` : ''}`,
          },
          {label: 'Banco', value: `${banco} · Ag ${agencia} · Cc ${conta}`},
        ].map(row => (
          <div
            key={row.label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid #f0f2f5',
            }}
          >
            <span style={{fontSize: 13, color: C.muted, fontWeight: 500}}>
              {row.label}
            </span>
            <span
              style={{
                fontSize: 13,
                color: C.text,
                fontWeight: 600,
                textAlign: 'right',
                maxWidth: '60%',
              }}
            >
              {row.value}
            </span>
          </div>
        ))}

        <div
          style={{
            marginTop: 20,
            padding: '12px 16px',
            borderRadius: 10,
            background: 'rgba(5,150,105,0.08)',
            border: '1px solid rgba(5,150,105,0.25)',
          }}
        >
          <p
            style={{margin: 0, fontSize: 13, color: C.success, fontWeight: 600}}
          >
            🎁 7 dias grátis antes da primeira cobrança
          </p>
          <p style={{margin: '4px 0 0', fontSize: 12, color: C.muted}}>
            Depois do teste, a cobrança segue o plano escolhido. Você pode
            cancelar quando quiser.
          </p>
        </div>

        {submitError && (
          <p
            style={{
              color: '#e53935',
              fontSize: 13,
              marginTop: 12,
              marginBottom: 0,
            }}
          >
            {submitError}
          </p>
        )}

        <NavRow
          onBack={() => setStep('bancario')}
          onNext={handleSubmit}
          nextLabel="Enviar cadastro"
          loading={submitting}
        />
      </Card>
    </SlideUp>
  );

  // ── STEP: Done ────────────────────────────────────────────────────────────
  const renderDone = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <div style={{textAlign: 'center', padding: '8px 0 16px'}}>
          <CheckCircleOutline
            style={{fontSize: 72, color: C.success, marginBottom: 12}}
          />
          <p style={{margin: 0, fontSize: 22, fontWeight: 800, color: C.text}}>
            Cadastro enviado!
          </p>
          <p
            style={{
              margin: '8px 0 24px',
              fontSize: 14,
              color: C.muted,
              lineHeight: 1.6,
            }}
          >
            Sua conta de gestor foi criada com {selectedPlano?.nome} e já está
            em período de testes grátis por 7 dias. Acesse o painel para começar
            a configurar tudo.
          </p>

          <Button
            href="https://myireit.com.br"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              bgcolor: C.laranja,
              color: '#fff',
              borderRadius: '12px',
              height: 52,
              px: 4,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 15,
              width: '100%',
              mb: 1,
              ':hover': {bgcolor: C.laranjaD},
            }}
          >
            Acessar painel do gestor →
          </Button>
          <Button
            component={Link}
            to="/"
            sx={{
              color: C.muted,
              borderRadius: '12px',
              height: 46,
              width: '100%',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              mt: 1,
              ':hover': {color: C.text},
            }}
          >
            Voltar ao início
          </Button>
        </div>
      </Card>
    </SlideUp>
  );

  // ── Prompt: nenhum plano escolhido ainda ─────────────────────────────────
  const renderEscolhaPrimeiro = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <div style={{textAlign: 'center', padding: '12px 0'}}>
          <p style={{margin: 0, fontSize: 18, fontWeight: 800, color: C.text}}>
            Escolha um plano para continuar
          </p>
          <p style={{margin: '8px 0 20px', fontSize: 14, color: C.muted}}>
            Selecione o plano ideal para o seu negócio logo acima.
          </p>
          <Button
            onClick={scrollToPricing}
            sx={{
              bgcolor: C.azul,
              color: '#fff',
              borderRadius: '12px',
              height: 46,
              px: 3,
              textTransform: 'none',
              fontWeight: 700,
              ':hover': {bgcolor: C.azulD},
            }}
          >
            Ver planos
          </Button>
        </div>
      </Card>
    </SlideUp>
  );

  const planosDoGrupo = PLANOS_CATALOG.filter(p => p.grupo === grupoAtivo);

  // ──────────────────────────────────────────────────────────────────────────
  // PAGE
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        overflowX: 'hidden',
        width: '100%',
        fontFamily: "'Outfit','Segoe UI',sans-serif",
      }}
    >
      {isVisible && (
        <Fab
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          style={{position: 'fixed', bottom: 20, left: 20, zIndex: 2}}
          size="small"
          color="primary"
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      {/* Navbar */}
      <div
        style={{
          background: C.azul,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          zIndex: 9999,
          top: 0,
          left: 0,
          width: '100%',
        }}
      >
        <Button
          component={Link}
          to="/"
          style={{
            width: 95,
            height: 47,
            marginLeft: 12,
            backgroundImage: `url(${logo})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        />
        {sm || vsm ? (
          <MenuIcon
            style={{color: '#fff', marginRight: 15}}
            onClick={() => setToggleBar(true)}
          />
        ) : (
          <Tab />
        )}
      </div>
      {(sm || vsm) && (
        <Drawer
          anchor="top"
          open={toggleBar}
          onClose={() => setToggleBar(false)}
        >
          <Tab setToggleBar={setToggleBar} />
        </Drawer>
      )}

      {/* HERO */}
      <div
        style={{
          background: `linear-gradient(145deg, ${C.azul} 0%, ${C.azul} 35%, ${C.roxo} 75%, ${C.roxo} 100%)`,
          paddingTop: sm ? 80 : 100,
          paddingBottom: sm ? 40 : 60,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 350,
            height: 350,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(255,102,0,0.1) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 260,
            height: 260,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(124,83,227,0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            maxWidth: 720,
            margin: '0 auto',
            padding: sm ? '0 16px' : '0 24px',
            textAlign: 'center',
          }}
        >
          <SlideUp delay="0.05s">
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 20,
                background: 'rgba(255,102,0,0.18)',
                border: '1px solid rgba(255,102,0,0.4)',
                borderRadius: 20,
                padding: '5px 16px',
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: C.laranja,
                  display: 'inline-block',
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.laranja,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Seja um gestor Irê it
              </span>
            </div>
          </SlideUp>

          <SlideUp delay="0.1s">
            <h1
              style={{
                color: '#fff',
                fontSize: sm ? 30 : 44,
                fontWeight: 800,
                margin: '0 0 12px',
                lineHeight: 1.1,
                letterSpacing: -0.5,
              }}
            >
              Confira os planos para o seu{' '}
              <span style={{color: C.laranja}}>negócio</span>
              <br />
              de mobilidade e delivery
            </h1>
          </SlideUp>

          <SlideUp delay="0.15s">
            <p
              style={{
                color: 'rgba(255,255,255,0.65)',
                fontSize: sm ? 15 : 18,
                margin: '0 auto 28px',
                maxWidth: 540,
                lineHeight: 1.55,
              }}
            >
              Do motorista solo à franquia regional — a tecnologia da Irê it com
              o plano certo para o seu tamanho. Cobrança fixa, por uso ou
              percentual: você escolhe.
            </p>
          </SlideUp>

          <SlideUp delay="0.2s">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                justifyContent: 'center',
                marginBottom: 8,
              }}
            >
              {benefits.map(b => (
                <span
                  key={b.text}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 20,
                    padding: '5px 12px',
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.85)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  {b.icon} {b.text}
                </span>
              ))}
            </div>
          </SlideUp>

          <SlideUp delay="0.25s">
            <Button
              onClick={scrollToPricing}
              sx={{
                mt: 3,
                bgcolor: C.laranja,
                color: '#fff',
                borderRadius: '12px',
                height: 52,
                px: 4,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 15,
                ':hover': {bgcolor: C.laranjaD},
              }}
            >
              Ver planos e preços
            </Button>
          </SlideUp>
        </div>
      </div>

      {/* PRICING */}
      <div
        ref={pricingRef}
        style={{background: C.bg, padding: sm ? '48px 12px' : '64px 24px'}}
      >
        <div style={{maxWidth: 1100, margin: '0 auto'}}>
          {/* Tabs de grupo */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              justifyContent: 'center',
              marginBottom: 36,
            }}
          >
            {GRUPOS.map(g => (
              <button
                key={g.id}
                onClick={() => setGrupoAtivo(g.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 20px',
                  borderRadius: 24,
                  border:
                    grupoAtivo === g.id
                      ? `2px solid ${C.azul}`
                      : '1px solid #e0e5ef',
                  background: grupoAtivo === g.id ? C.azul : '#fff',
                  color: grupoAtivo === g.id ? '#fff' : C.text,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: "'Outfit','Segoe UI',sans-serif",
                  transition: 'all 0.2s',
                }}
              >
                <span>{g.icon}</span>
                {g.label}
              </button>
            ))}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: sm
                ? '1fr'
                : 'repeat(auto-fit, minmax(270px, 1fr))',
              gap: 20,
              alignItems: 'stretch',
            }}
          >
            {planosDoGrupo.map(plano => (
              <PricingCard
                key={plano.nome}
                plano={plano}
                selected={selectedPlano?.nome === plano.nome}
                onEscolher={handleEscolherPlano}
                sm={sm}
              />
            ))}
            {planosDoGrupo.length === 0 && (
              <p
                style={{
                  textAlign: 'center',
                  color: C.muted,
                  gridColumn: '1 / -1',
                }}
              >
                Nenhum plano nessa categoria no momento.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* WIZARD */}
      <div
        ref={wizardRef}
        style={{
          background: '#eef1f7',
          padding: sm ? '32px 12px 60px' : '48px 24px 80px',
        }}
      >
        {selectedPlano &&
          step &&
          !['done', 'existing', 'redirect'].includes(step) && (
            <div style={{maxWidth: 620, margin: '0 auto 8px'}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#fff',
                  borderRadius: 12,
                  padding: '10px 16px',
                  marginBottom: 24,
                  border: '1px solid #e0e5ef',
                }}
              >
                <span style={{fontSize: 13, color: C.text}}>
                  Plano selecionado: <strong>{selectedPlano.nome}</strong>
                </span>
                <Button
                  onClick={scrollToPricing}
                  size="small"
                  style={{
                    textTransform: 'none',
                    color: C.laranja,
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  Trocar
                </Button>
              </div>
            </div>
          )}

        {step && !['done', 'existing', 'redirect'].includes(step) && (
          <div style={{maxWidth: 620, margin: '0 auto'}}>
            <StepBar current={step} sm={sm} />
          </div>
        )}

        <div style={{maxWidth: 520, margin: '0 auto'}}>
          {!step && renderEscolhaPrimeiro()}
          {step === 'auth' && renderAuth()}
          {step === 'existing' && renderExisting()}
          {step === 'redirect' && renderRedirect()}
          {step === 'empresa' && renderEmpresa()}
          {step === 'endereco' && renderEndereco()}
          {step === 'bancario' && renderBancario()}
          {step === 'confirm' && renderConfirm()}
          {step === 'done' && renderDone()}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterGestor;
